import { useState } from 'react';

import Countdown from 'react-countdown';
import { useForm } from 'react-hook-form';
import Turnstile from 'react-turnstile';
import * as Yup from 'yup';

import config from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Text, Group, Button, TextInput, showNotification } from '@ui';

interface OTPForm {
  otp: string;
}

interface PolicyVerificationOTPStepProps {
  onBack: () => void;
  onVerify: (otp: string) => void;
  onResend?: (token: string) => Promise<{ success: boolean; message: string }>;
  loading?: boolean;
  successMessage?: string;
}

// OTP validation schema
const otpValidationSchema = Yup.object().shape({
  otp: Yup.string()
    .required('OTP is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(6, 'Must be exactly 6 digits')
    .max(6, 'Must be exactly 6 digits')
});

export const PolicyVerificationOTPStep: React.FC<PolicyVerificationOTPStepProps> = ({
  onBack,
  onVerify,
  onResend,
  loading = false,
  successMessage
}) => {
  const [resendTurnstileToken, setResendTurnstileToken] = useState<string>('');
  const [turnstileError, setTurnstileError] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [countdownKey, setCountdownKey] = useState<number>(Date.now());

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch
  } = useForm<OTPForm>({
    resolver: yupResolver(otpValidationSchema),
    mode: 'onChange',
    defaultValues: {
      otp: ''
    }
  });

  const otpValue = watch('otp');

  const resetTurnstileStates = () => {
    setTurnstileError(false);
    setResendTurnstileToken('');
    setResendLoading(false);
  };

  const handleResendClick = async () => {
    if (!onResend || !resendTurnstileToken) return;

    setResendLoading(true);
    const result = await onResend(resendTurnstileToken);
    if (result.success) {
      showNotification({
        title: 'Success',
        message: result.message,
        color: 'green'
      });
      setResendTurnstileToken('');
      setCountdownKey(Date.now());
    }
    setResendLoading(false);
  };

  const countDownRenderer = ({
    seconds,
    completed
  }: {
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      return (
        <Group justify="center">
          <div aria-label="Security verification widget for resending OTP">
            <Turnstile
              sitekey={config.TURN_STILE_TOKEN}
              onVerify={(token: string) => {
                setResendTurnstileToken(token);
                setTurnstileError(false);
              }}
              onError={() => {
                setTurnstileError(true);
                setResendTurnstileToken('');
              }}
            />
          </div>
        </Group>
      );
    }
    return (
      <Text fz={14} c="dimmed">
        Resend OTP in{' '}
        <Text component="span" fw={600} c="primary">
          {seconds}s
        </Text>
      </Text>
    );
  };

  const onSubmit = (data: OTPForm) => {
    onVerify(data.otp);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={24}>
        <Text fz={14} lh={1.5}>
          {successMessage ??
            'An OTP was sent to your registered mobile number successfully.'}
        </Text>

        <TextInput
          {...register('otp')}
          label="Enter OTP"
          placeholder="Enter 6-digit OTP"
          size="xl"
          withAsterisk
          maxLength={6}
          error={errors.otp?.message}
          autoFocus
        />

        <Group justify="center">
          <Countdown
            key={`countdown-${countdownKey}`}
            date={countdownKey + 30000}
            renderer={countDownRenderer}
          />
        </Group>

        {turnstileError && (
          <Stack gap={8} align="center">
            <Text fz={14} c="red" ta="center">
              Security verification failed. Please try again.
            </Text>
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={resetTurnstileStates}
            >
              Retry
            </Button>
          </Stack>
        )}

        {resendTurnstileToken && onResend && (
          <Group justify="center">
            <Button
              onClick={handleResendClick}
              variant="subtle"
              size="sm"
              disabled={resendLoading || !resendTurnstileToken}
              loading={resendLoading}
            >
              Resend OTP
            </Button>
          </Group>
        )}

        <Group gap="xs" wrap="nowrap">
          <Button onClick={onBack} variant="outline" fullWidth size="xl">
            Back
          </Button>
          <Button
            type="submit"
            fullWidth
            size="xl"
            loading={loading}
            disabled={loading || !isValid || otpValue.length !== 6}
          >
            Verify
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
